import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import { BRAND_COLORS, TEXT_COLORS, BACKGROUND_COLORS, BORDER_COLORS } from '../../style/colors.js';
import { formatDate, formatNumber, cleanUserName, formatPiedrasForModal } from '../../services/Functions';
import { translateText } from '../../translations/translationService';
import { EXTERNO_PRECIOS } from '../../constants/Constants.js';
import { generateBulkAssignmentPDF, generateSingleAssignmentPDF } from '../../services/api/pdf/PdfApi.js';
import { validateStonesMeasures, validateWorkOrder } from '../../services/api/pdf/ValidationApi.js';
import { useEffect } from 'react';
import { notify } from '../../services/ui/notify';
import AppDialog from '../Common/ui/AppDialog';
import AppButton from '../Common/ui/AppButton';

const ComponentExternoEngastadorBulkModal = ({ 
  open, 
  onClose, 
  moDataList, 
  userName, 
  externoType,
  onAssign,
  lang = 'es' 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [fechaAsignacion, setFechaAsignacion] = useState(new Date());
  const generatedByName = useSelector((state) => state.user.name);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [workOrderResult, setWorkOrderResult] = useState(null);
  const [stoneMeasures, setStoneMeasures] = useState({}); // { "MO": { "piedraCode": "medida" } }
  const [stonePrices, setStonePrices] = useState({}); // { "MO": { "piedraCode": "precio" } }
  const [repairMeasures, setRepairMeasures] = useState({}); // { "MO": [ { numeroPiedras, medida1, medida2, precioManual? } ] }
  const [preciosManuales, setPreciosManuales] = useState({}); // { "MO": { "codigoPiedra": "" } } - solo override opcional

  // Overrides opcionales para editar cantidades (cantidad_mo y cantidad por Ref Piedra) en modo bulk
  const [originalCantidadMos, setOriginalCantidadMos] = useState({});
  const [cantidadMoOverrides, setCantidadMoOverrides] = useState({});
  const [originalPiedrasCantidadByMo, setOriginalPiedrasCantidadByMo] = useState({});
  const [piedrasCantidadOverridesByMo, setPiedrasCantidadOverridesByMo] = useState({});

  // Confirmación (reemplaza window.confirm)
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const confirmResolverRef = useRef(null);

  const askConfirm = (message) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmMessage(message);
      setConfirmVisible(true);
    });
  };

  useEffect(() => {
    if (open && moDataList && moDataList.length > 0) {
      const validateData = async () => {
        setIsValidating(true);
        try {
          // Validar medidas de piedras
          const result = await validateStonesMeasures(moDataList);
          setValidationResult(result);
          
          if (result) {
            const initialMeasures = {};
            const initialPrices = {};
            Object.keys(result).forEach((mo) => {
              initialMeasures[mo] = {};
              initialPrices[mo] = {};
              Object.keys(result[mo]).forEach((piedraCode) => {
                if (result[mo][piedraCode].valid === false) {
                  initialMeasures[mo][piedraCode] = '';
                }
                if (result[mo][piedraCode].valid === true && result[mo][piedraCode].hasPrice === false) {
                  initialPrices[mo][piedraCode] = '';
                }
              });
            });
            setStoneMeasures(initialMeasures);
            setStonePrices(initialPrices);
          }

          // Validar orden de trabajo
          const workOrderResult = await validateWorkOrder(moDataList);
          setWorkOrderResult(workOrderResult);

          // Inicializar overrides con los valores actuales
          const originalCantidadMap = {};
          const cantidadOverridesMap = {};
          const originalPiedrasMap = {};
          const piedrasOverridesMap = {};

          for (const moItem of moDataList) {
            const moKey = moItem?.mo;
            const parsedCantidadMo = parseFloat(moItem?.cantidad ?? 0);
            const safeCantidadMo = Number.isNaN(parsedCantidadMo) ? 0 : parsedCantidadMo;

            originalCantidadMap[moKey] = safeCantidadMo;
            cantidadOverridesMap[moKey] = String(safeCantidadMo);

            const piedrasBase = formatPiedrasForModal(moItem, EXTERNO_PRECIOS);
            const originalPiedras = {};
            const piedrasOverrides = {};

            piedrasBase.forEach((p) => {
              const qty = parseFloat(p?.cantidad ?? 0);
              const safeQty = Number.isNaN(qty) ? 0 : qty;
              originalPiedras[p.codigo] = safeQty;
              piedrasOverrides[p.codigo] = String(safeQty);
            });

            originalPiedrasMap[moKey] = originalPiedras;
            piedrasOverridesMap[moKey] = piedrasOverrides;
          }

          setOriginalCantidadMos(originalCantidadMap);
          setCantidadMoOverrides(cantidadOverridesMap);
          setOriginalPiedrasCantidadByMo(originalPiedrasMap);
          setPiedrasCantidadOverridesByMo(piedrasOverridesMap);
        } catch (error) {
          console.error('Error validando datos:', error);
          setValidationResult(null);
          setWorkOrderResult(null);
        } finally {
          setIsValidating(false);
        }
      };
      
      validateData();
    } else {
      setValidationResult(null);
      setWorkOrderResult(null);
      setStoneMeasures({});
      setStonePrices({});
      setRepairMeasures({});
      setPreciosManuales({});

      setOriginalCantidadMos({});
      setCantidadMoOverrides({});
      setOriginalPiedrasCantidadByMo({});
      setPiedrasCantidadOverridesByMo({});
    }
  }, [open, moDataList]);

  const getStonesNeedingMeasures = (mo) => {
    if (!validationResult || !validationResult[mo]) return [];
    return Object.keys(validationResult[mo]).filter(
      piedraCode => validationResult[mo][piedraCode].valid === false
    );
  };

  const getStonesNeedingPrices = (mo) => {
    if (!validationResult || !validationResult[mo]) return [];
    return Object.keys(validationResult[mo]).filter(
      piedraCode => validationResult[mo][piedraCode].valid === true && validationResult[mo][piedraCode].hasPrice === false
    );
  };

  const isRepairRowValid = (row) => {
    const numero = Number(row.numeroPiedras);
    const medida1 = Number(row.medida1);
    const medida2 = Number(row.medida2);

    if (Number.isNaN(numero) || Number.isNaN(medida1) || Number.isNaN(medida2)) {
      return false;
    }

    return numero > 0 && medida1 > 0 && medida2 > 0;
  };

  const hasValidRepairDataForMo = (mo) => {
    const rows = repairMeasures[mo];
    if (!rows || rows.length === 0) {
      return false;
    }

    return rows.some((row) => isRepairRowValid(row));
  };

  const handleRepairRowChange = (mo, index, field, value) => {
    setRepairMeasures((prev) => {
      const currentRows =
        prev[mo] && prev[mo].length > 0
          ? [...prev[mo]]
          : [{ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' }];

      currentRows[index] = {
        ...currentRows[index],
        [field]: value
      };

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const handleAddRepairRow = (mo) => {
    setRepairMeasures((prev) => {
      const currentRows =
        prev[mo] && prev[mo].length > 0
          ? [...prev[mo]]
          : [{ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' }];

      currentRows.push({ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' });

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const handleRemoveRepairRow = (mo, index) => {
    setRepairMeasures((prev) => {
      const currentRows = prev[mo] ? [...prev[mo]] : [];

      if (currentRows.length <= 1) {
        return prev;
      }

      currentRows.splice(index, 1);

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const allMeasuresFilled = () => {
    if (!validationResult) return true;
    
    for (const mo of Object.keys(validationResult)) {
      const stonesNeedingMeasures = getStonesNeedingMeasures(mo);
      for (const piedraCode of stonesNeedingMeasures) {
        if (!stoneMeasures[mo] || !stoneMeasures[mo][piedraCode] || stoneMeasures[mo][piedraCode].trim() === '') {
          return false;
        }
      }
    }
    return true;
  };

  const allPricesFilled = () => {
    if (!validationResult) return true;
    
    for (const mo of Object.keys(validationResult)) {
      const stonesNeedingPrices = getStonesNeedingPrices(mo);
      for (const piedraCode of stonesNeedingPrices) {
        if (!stonePrices[mo] || !stonePrices[mo][piedraCode] || stonePrices[mo][piedraCode].trim() === '') {
          return false;
        }
      }
    }
    return true;
  };

  const allRequiredDataFilled = () => {
    const baseValid = allMeasuresFilled() && allPricesFilled();

    if (!moDataList || moDataList.length === 0) {
      return baseValid;
    }

    const allRepairValid = moDataList.every((mo) => {
      const piedras = formatPiedrasForModal(mo, EXTERNO_PRECIOS);
      const isRepairWithoutStones =
        mo.mo && mo.mo.toString().startsWith('8') && piedras.length === 0;

      if (!isRepairWithoutStones) {
        return true;
      }

      return hasValidRepairDataForMo(mo.mo);
    });

    return baseValid && allRepairValid;
  };

  const handleMeasureChange = (mo, piedraCode, value) => {
    setStoneMeasures(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [piedraCode]: value
      }
    }));
  };

  const handlePriceChange = (mo, piedraCode, value) => {
    setStonePrices(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [piedraCode]: value
      }
    }));
  };

  const handlePrecioManualChange = (mo, codigoPiedra, value) => {
    setPreciosManuales(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [codigoPiedra]: value
      }
    }));
  };

  const handleCantidadMoOverrideChange = (mo, value) => {
    setCantidadMoOverrides((prev) => ({
      ...prev,
      [mo]: value
    }));
  };

  const handlePiedraCantidadOverrideChange = (mo, piedraCodigo, value) => {
    setPiedrasCantidadOverridesByMo((prev) => ({
      ...prev,
      [mo]: {
        ...(prev[mo] || {}),
        [piedraCodigo]: value
      }
    }));
  };

  const generateSinglePDF = async (moData) => {
    setIsLoading(true);
    try {
      const piedrasForMo = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
      const isRepairWithoutStones =
        moData.mo && moData.mo.toString().startsWith('8') && piedrasForMo.length === 0;

      let repairMeasuresPayload = {};

      if (isRepairWithoutStones) {
        const rows = (repairMeasures[moData.mo] || []).filter((row) =>
          isRepairRowValid(row)
        );

        if (rows.length > 0) {
          repairMeasuresPayload = {
            [moData.mo]: rows.map((row) => {
              const line = {
                numeroPiedras: Number(row.numeroPiedras),
                medida1: Number(row.medida1),
                medida2: Number(row.medida2)
              };
              const precio = parseFloat(row.precioManual);
              if (!Number.isNaN(precio) && precio > 0) {
                line.precioManual = precio;
              }
              return line;
            })
          };
        }
      }

      const preciosManualesPayload = {};
      const manualesForMo = preciosManuales[moData.mo];
      if (manualesForMo && typeof manualesForMo === 'object') {
        const filtered = {};
        Object.keys(manualesForMo).forEach(codigo => {
          const val = parseFloat(manualesForMo[codigo]);
          if (!Number.isNaN(val) && val > 0) {
            filtered[codigo] = val;
          }
        });
        if (Object.keys(filtered).length > 0) {
          preciosManualesPayload[moData.mo] = filtered;
        }
      }
      
      const result = await generateSingleAssignmentPDF(
        userName,
        moData.mo,
        fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion,
        generatedByName,
        stoneMeasures,
        stonePrices,
        repairMeasuresPayload,
        preciosManualesPayload
      );
      
      if (result && result.pdfBlob) {
        const { pdfBlob, filename } = result;
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        notify.error('Error generando PDF. Contacta al administrador.');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      notify.error('Error generando PDF. Contacta al administrador.');
    } finally {
      setIsLoading(false);
    }
  };

  // const generateAllPDFs = async () => {
  //   setIsLoading(true);
  //   try {
  //     const moList = moDataList.map(mo => mo.mo);
      
  //     const result = await generateBulkAssignmentPDF(
  //       userName,
  //       moList,
  //       fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion,
  //       generatedByName
  //     );
      
  //     if (!result || result === false) {
  //       notify.error('Error generando PDF. Contacta al administrador.');
  //       return;
  //     }
      
  //     const { pdfBlob, filename } = result;
  //     const finalFilename = filename || `Asignaciones_Engastador_Consolidada_${new Date().toISOString().split('T')[0]}.pdf`;
      
  //     if (pdfBlob) {
  //       const url = URL.createObjectURL(pdfBlob);
  //       const link = document.createElement('a');
  //       link.href = url;
  //       link.download = finalFilename;
  //       link.style.display = 'none';
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       URL.revokeObjectURL(url);
  //     } else {
  //       notify.error('Error generando PDF. Contacta al administrador.');
  //     }
  //   } catch (error) {
  //     console.error('Error generando PDF:', error);
  //     notify.error('Error generando PDF. Contacta al administrador.');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleAssignAndGeneratePDF = async () => {
    if (!onAssign) {
      console.warn('Función onAssign no proporcionada');
      return;
    }

    if (!allRequiredDataFilled()) {
      notify.warn('Por favor, completa todas las medidas y precios faltantes antes de asignar.');
      return;
    }

    // Verificar si alguna MO tiene orden de trabajo
    if (workOrderResult) {
      const moWithWorkOrder = moDataList.filter(mo => workOrderResult[mo.mo]);
      if (moWithWorkOrder.length > 0) {
        const moList = moWithWorkOrder.map(mo => {
          const woNumber = workOrderResult[mo.mo];
          return `${mo.mo} (${woNumber})`;
        }).join(', ');
        
        const confirmReplace = await askConfirm(
          `Las MO ${moList} ya tienen una orden de trabajo, ¿Quiere reemplazarla?`
        );
        if (!confirmReplace) return; // Cancela la operación
      }
    }

    setIsAssigning(true);
    try {
      await onAssign(moDataList, userName);
      
      const moList = moDataList.map(mo => mo.mo);

      const repairMeasuresPayload = {};

      moDataList.forEach((mo) => {
        const piedras = formatPiedrasForModal(mo, EXTERNO_PRECIOS);
        const isRepairWithoutStones =
          mo.mo && mo.mo.toString().startsWith('8') && piedras.length === 0;

        if (isRepairWithoutStones) {
          const rows = (repairMeasures[mo.mo] || []).filter((row) =>
            isRepairRowValid(row)
          );

          if (rows.length > 0) {
            repairMeasuresPayload[mo.mo] = rows.map((row) => {
              const line = {
                numeroPiedras: Number(row.numeroPiedras),
                medida1: Number(row.medida1),
                medida2: Number(row.medida2)
              };
              const precio = parseFloat(row.precioManual);
              if (!Number.isNaN(precio) && precio > 0) {
                line.precioManual = precio;
              }
              return line;
            });
          }
        }
      });

      const preciosManualesPayload = {};
      Object.keys(preciosManuales).forEach(mo => {
        const manualesForMo = preciosManuales[mo];
        if (manualesForMo && typeof manualesForMo === 'object') {
          const filtered = {};
          Object.keys(manualesForMo).forEach(codigo => {
            const val = parseFloat(manualesForMo[codigo]);
            if (!Number.isNaN(val) && val > 0) {
              filtered[codigo] = val;
            }
          });
          if (Object.keys(filtered).length > 0) {
            preciosManualesPayload[mo] = filtered;
          }
        }
      });

      // Construir entrega_overrides solo si el usuario cambió cantidad_mo o alguna cantidad por Ref Piedra
      const entregaOverridesFinal = {};

      for (const moItem of moDataList) {
        const moKey = moItem.mo;

        const parsedCantidadMo = parseFloat(cantidadMoOverrides[moKey]);
        if (Number.isNaN(parsedCantidadMo) || parsedCantidadMo < 0) {
          notify.error(`La cantidad de piezas (cantidad_mo) para la MO ${moKey} no es válida.`);
          return;
        }

        const originalCantidadMo =
          originalCantidadMos[moKey] ?? parseFloat(moItem?.cantidad ?? 0);
        const originalCantidadMoSafe = Number.isNaN(originalCantidadMo) ? 0 : originalCantidadMo;

        const cantidadMoChanged = parsedCantidadMo !== originalCantidadMoSafe;

        const piedras = formatPiedrasForModal(moItem, EXTERNO_PRECIOS);
        const piedrasOverridesPayload = {};
        let anyPiedraChanged = false;

        for (const piedra of piedras) {
          const raw =
            piedrasCantidadOverridesByMo[moKey]?.[piedra.codigo] ??
            String(piedra.cantidad);
          const parsed = parseFloat(raw);
          if (Number.isNaN(parsed) || parsed < 0) {
            notify.error(
              `La cantidad de piedra (${piedra.codigo}) para la MO ${moKey} no es válida.`
            );
            return;
          }

          const originalQty =
            originalPiedrasCantidadByMo[moKey]?.[piedra.codigo] ??
            parseFloat(piedra.cantidad);
          const originalQtySafe = Number.isNaN(originalQty) ? 0 : originalQty;

          if (parsed !== originalQtySafe) {
            anyPiedraChanged = true;
            piedrasOverridesPayload[piedra.codigo] = parsed;
          }
        }

        const moOverrides = {};
        if (cantidadMoChanged) moOverrides.cantidad_mo = parsedCantidadMo;
        if (anyPiedraChanged) moOverrides.piedras = piedrasOverridesPayload;

        if (Object.keys(moOverrides).length > 0) {
          entregaOverridesFinal[moKey] = moOverrides;
        }
      }

      const entregaOverridesFinalPayload =
        Object.keys(entregaOverridesFinal).length > 0 ? entregaOverridesFinal : null;

      const result = await generateBulkAssignmentPDF(
        userName,
        moList,
        fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion,
        generatedByName,
        stoneMeasures,
        stonePrices,
        repairMeasuresPayload,
        preciosManualesPayload,
        entregaOverridesFinalPayload
      );
      
      if (result && result.pdfBlob) {
        const { pdfBlob, filename } = result;
        const finalFilename = filename || `Asignaciones_Engastador_Consolidada_${new Date().toISOString().split('T')[0]}.pdf`;
        
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFilename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      onClose();
    } catch (error) {
      console.error('Error en la asignación o generación de PDF:', error);
      notify.error('Error en el proceso. Contacta al administrador.');
    } finally {
      setIsAssigning(false);
    }
  };

  const totalPiedras = moDataList.reduce((total, moData) => {
    const piedras = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
    return (
      total +
      piedras.reduce((sum, piedra) => {
        const raw =
          piedrasCantidadOverridesByMo[moData.mo]?.[piedra.codigo] ??
          String(piedra.cantidad);
        const qty = parseFloat(raw);
        return sum + (Number.isNaN(qty) ? 0 : qty);
      }, 0)
    );
  }, 0);

  return (
    <>
      <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          backgroundColor: BACKGROUND_COLORS.PRIMARY
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: BRAND_COLORS.PRIMARY,
          color: 'white',
          padding: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Asignación Múltiple a Engastador Externo
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 3, paddingTop: 4 }}>
        <Box sx={{ mb: 4, mt: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY }}>
              <strong>Usuario:</strong> {cleanUserName(userName)}
            </Typography>
            <Chip
              label="Engastador Externo"
              color="primary"
              size="small"
            />
            <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY }}>
              {moDataList.length} pieza{moDataList.length !== 1 ? 's' : ''} seleccionada{moDataList.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY, minWidth: 'fit-content' }}>
              <strong>Fecha de Asignación:</strong>
            </Typography>
            <TextField
              type="date"
              value={fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion}
              onChange={(e) => setFechaAsignacion(new Date(e.target.value))}
              size="small"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  height: '32px',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5, 
          justifyContent: 'center',
          maxWidth: '100%'
        }}>
          {moDataList.map((moData, index) => {
            const piedras = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
            const totalPiedrasMO = piedras.reduce((sum, piedra) => {
              const raw =
                piedrasCantidadOverridesByMo[moData.mo]?.[piedra.codigo] ??
                String(piedra.cantidad);
              const qty = parseFloat(raw);
              return sum + (Number.isNaN(qty) ? 0 : qty);
            }, 0);
            const isRepairWithoutStones =
              moData.mo && moData.mo.toString().startsWith('8') && totalPiedrasMO === 0;
            const repairRowsForMo =
              repairMeasures[moData.mo] && repairMeasures[moData.mo].length > 0
                ? repairMeasures[moData.mo]
                : [{ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' }];

            return (
              <Card
                key={moData.key || index}
                sx={{
                  border: `2px solid ${BORDER_COLORS.PRIMARY}`,
                  width: 270,
                  minHeight: 260, // Altura mínima, pero puede crecer
                  maxHeight: 400, // Altura máxima para evitar tarjetas muy grandes
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    padding: 1.5, 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: 'center',
                          fontWeight: 600,
                          color: BRAND_COLORS.PRIMARY,
                          fontSize: '1rem'
                        }}
                      >
                        {moData.mo}
                      </Typography>
                      {workOrderResult && workOrderResult[moData.mo] && (
                        <Tooltip title={`Ya tiene orden de trabajo: ${workOrderResult[moData.mo]}`}>
                          <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY, mb: 0.5, fontSize: '0.8rem' }}>
                        <strong>Ref:</strong> {moData.referencia}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: TEXT_COLORS.SECONDARY,
                          mb: 0.5,
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <strong>Cantidad:</strong>
                        <TextField
                          type="number"
                          size="small"
                          value={cantidadMoOverrides[moData.mo] ?? String(moData.cantidad)}
                          onChange={(e) =>
                            handleCantidadMoOverrideChange(moData.mo, e.target.value)
                          }
                          inputProps={{ min: 0, step: 1 }}
                          sx={{
                            width: 86,
                            '& .MuiOutlinedInput-root': { height: 28 },
                            '& input[type=number]': { MozAppearance: 'textfield' },
                            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                              { WebkitAppearance: 'none', margin: 0 },
                          }}
                        />
                      </Typography>
                    </Box>

                    {isRepairWithoutStones && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.4, fontSize: '0.7rem' }}
                        >
                          {translateText('Datos manuales reparación', lang)}
                        </Typography>

                        {repairRowsForMo.map((row, rowIndex) => (
                          <Box
                            key={rowIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.4,
                              mb: 0.6,
                              flexWrap: 'nowrap'
                            }}
                          >
                            <TextField
                              label={translateText('Nº', lang)}
                              type="number"
                              size="small"
                              value={row.numeroPiedras}
                              onChange={(e) =>
                                handleRepairRowChange(
                                  moData.mo,
                                  rowIndex,
                                  'numeroPiedras',
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 52,
                                minWidth: 52,
                                '& .MuiOutlinedInput-root': { height: 28 },
                                '& .MuiInputLabel-root': { fontSize: '0.65rem' },
                                '& .MuiOutlinedInput-input': { fontSize: '0.7rem', padding: '4px 6px' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none', margin: 0
                                }
                              }}
                              inputProps={{ min: 0 }}
                            />
                            <TextField
                              label={translateText('1ª mm', lang)}
                              type="number"
                              size="small"
                              value={row.medida1}
                              onChange={(e) =>
                                handleRepairRowChange(
                                  moData.mo,
                                  rowIndex,
                                  'medida1',
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 56,
                                minWidth: 56,
                                '& .MuiOutlinedInput-root': { height: 28 },
                                '& .MuiInputLabel-root': { fontSize: '0.65rem' },
                                '& .MuiOutlinedInput-input': { fontSize: '0.7rem', padding: '4px 6px' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none', margin: 0
                                }
                              }}
                              inputProps={{ min: 0, step: '0.01' }}
                            />
                            <TextField
                              label={translateText('2ª mm', lang)}
                              type="number"
                              size="small"
                              value={row.medida2}
                              onChange={(e) =>
                                handleRepairRowChange(
                                  moData.mo,
                                  rowIndex,
                                  'medida2',
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 56,
                                minWidth: 56,
                                '& .MuiOutlinedInput-root': { height: 28 },
                                '& .MuiInputLabel-root': { fontSize: '0.65rem' },
                                '& .MuiOutlinedInput-input': { fontSize: '0.7rem', padding: '4px 6px' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none', margin: 0
                                }
                              }}
                              inputProps={{ min: 0, step: '0.01' }}
                            />
                            <TextField
                              label={translateText('Precio', lang)}
                              type="number"
                              size="small"
                              value={row.precioManual ?? ''}
                              onChange={(e) =>
                                handleRepairRowChange(
                                  moData.mo,
                                  rowIndex,
                                  'precioManual',
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 64,
                                minWidth: 64,
                                '& .MuiOutlinedInput-root': { height: 28 },
                                '& .MuiInputLabel-root': { fontSize: '0.65rem' },
                                '& .MuiOutlinedInput-input': { fontSize: '0.7rem', padding: '4px 6px' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none', margin: 0
                                }
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />

                            {repairRowsForMo.length > 1 && (
                              <IconButton
                                aria-label="remove-repair-row"
                                onClick={() => handleRemoveRepairRow(moData.mo, rowIndex)}
                                size="small"
                                sx={{ p: 0.25 }}
                              >
                                <RemoveCircleOutlineIcon color="error" fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.25 }}>
                          <Button
                            startIcon={<AddCircleOutlineIcon sx={{ fontSize: '0.9rem' }} />}
                            size="small"
                            variant="text"
                            onClick={() => handleAddRepairRow(moData.mo)}
                            sx={{ fontSize: '0.65rem', textTransform: 'none', p: 0.25, minWidth: 0 }}
                          >
                            {translateText('Añadir línea', lang)}
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {piedras.length > 0 && (
                      <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        minHeight: 0 // Importante para que funcione el scroll
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.8rem' }}>
                          Piedras:
                        </Typography>
                        <Box sx={{ 
                          flexGrow: 1,
                          maxHeight: (getStonesNeedingMeasures(moData.mo).length > 0 || getStonesNeedingPrices(moData.mo).length > 0) ? 200 : 100,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          paddingRight: '8px',
                          '&::-webkit-scrollbar': {
                            width: '3px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '2px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '2px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: '#a8a8a8',
                          }
                        }}>
                          {piedras.map((piedra, idx) => {
                            const needsMeasure = validationResult && 
                                              validationResult[moData.mo] && 
                                              validationResult[moData.mo][piedra.codigo] &&
                                              validationResult[moData.mo][piedra.codigo].valid === false;
                            
                            const needsPrice = validationResult && 
                                            validationResult[moData.mo] && 
                                            validationResult[moData.mo][piedra.codigo] &&
                                            validationResult[moData.mo][piedra.codigo].valid === true &&
                                            validationResult[moData.mo][piedra.codigo].hasPrice === false;
                            
                            const minMeasure = needsPrice && validationResult[moData.mo][piedra.codigo].minMeasure;
                            
                            const needsInput = needsMeasure || needsPrice;
                            
                            return (
                              <Box key={idx} sx={{ mb: needsInput ? 1 : 0.3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: needsInput ? 0.5 : 0 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                    {piedra.codigo}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      piedrasCantidadOverridesByMo[moData.mo]?.[piedra.codigo] ??
                                      String(piedra.cantidad)
                                    }
                                    onChange={(e) =>
                                      handlePiedraCantidadOverrideChange(
                                        moData.mo,
                                        piedra.codigo,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{ min: 0, step: 1 }}
                                    sx={{
                                      width: 64,
                                      minWidth: 64,
                                      '& .MuiOutlinedInput-root': { height: 22 },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '0.65rem',
                                        padding: '2px 4px',
                                        textAlign: 'center'
                                      },
                                      '& input[type=number]': { MozAppearance: 'textfield' },
                                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                                        { WebkitAppearance: 'none', margin: 0 }
                                    }}
                                  />
                                </Box>
                                <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: TEXT_COLORS.SECONDARY, flexShrink: 0 }}>
                                    {translateText('Precio manual (opc.)', lang)}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    placeholder="—"
                                    value={preciosManuales[moData.mo]?.[piedra.codigo] ?? ''}
                                    onChange={(e) => handlePrecioManualChange(moData.mo, piedra.codigo, e.target.value)}
                                    sx={{
                                      width: 56,
                                      minWidth: 56,
                                      '& .MuiOutlinedInput-root': { height: 22 },
                                      '& .MuiOutlinedInput-input': { fontSize: '0.65rem', padding: '2px 4px' },
                                      '& input[type=number]': { MozAppearance: 'textfield' },
                                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none', margin: 0
                                      }
                                    }}
                                    inputProps={{ min: 0, step: '0.01' }}
                                  />
                                </Box>
                                {needsMeasure && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#856404', mb: 0.3, display: 'block' }}>
                                      Medida para {piedra.codigo}:
                                    </Typography>
                                    <TextField
                                      size="small"
                                      placeholder=""
                                      value={stoneMeasures[moData.mo]?.[piedra.codigo] || ''}
                                      onChange={(e) => handleMeasureChange(moData.mo, piedra.codigo, e.target.value)}
                                      sx={{
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                          height: '28px',
                                          fontSize: '0.65rem',
                                          backgroundColor: '#fff3cd'
                                        }
                                      }}
                                      inputProps={{
                                        style: { fontSize: '0.65rem', padding: '4px 8px' }
                                      }}
                                    />
                                  </Box>
                                )}
                                {needsPrice && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#856404', mb: 0.3, display: 'block' }}>
                                      Precio para {piedra.codigo}{minMeasure ? `, ${minMeasure} mm` : ''}:
                                    </Typography>
                                    <TextField
                                      size="small"
                                      type="number"
                                      placeholder=""
                                      value={stonePrices[moData.mo]?.[piedra.codigo] || ''}
                                      onChange={(e) => handlePriceChange(moData.mo, piedra.codigo, e.target.value)}
                                      sx={{
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                          height: '28px',
                                          fontSize: '0.65rem',
                                          backgroundColor: '#fff3cd'
                                        }
                                      }}
                                      inputProps={{
                                        style: { fontSize: '0.65rem', padding: '4px 8px' },
                                        step: '0.01',
                                        min: '0'
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        <Box sx={{ 
                          borderTop: '1px solid #e0e0e0', 
                          mt: 0.5, 
                          pt: 0.5,
                          flexShrink: 0 // Evita que se comprima
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', fontSize: '0.8rem' }}>
                            Total: {totalPiedrasMO}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                      {/* <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PdfIcon sx={{ fontSize: '0.8rem' }} />}
                        onClick={() => generateSinglePDF(moData)}
                        sx={{
                          borderColor: BRAND_COLORS.PRIMARY,
                          color: BRAND_COLORS.PRIMARY,
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          marginTop:1,
                          minWidth: '60px',
                          height: '28px',
                          '&:hover': {
                            backgroundColor: BRAND_COLORS.PRIMARY,
                            color: 'white'
                          }
                        }}
                      >
                        PDF
                      </Button> */}
                    </Box>
                  </CardContent>
                </Card>
            );
          })}
        </Box>

        <Box sx={{ mt: 3, p: 2, backgroundColor: BACKGROUND_COLORS.SECONDARY, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
            Total Piedras: {totalPiedras}
          </Typography>
        </Box>

        {isValidating && (
          <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY }}>
              Validando piedras...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 2, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: BORDER_COLORS.PRIMARY,
            color: TEXT_COLORS.PRIMARY,
            textTransform: 'none'
          }}
        >
          {translateText('Cerrar', lang)}
        </Button>
        <Button
          onClick={handleAssignAndGeneratePDF}
          variant="contained"
          startIcon={<AssignmentIcon />}
          disabled={isLoading || isAssigning || isValidating || !allRequiredDataFilled()}
          sx={{
            backgroundColor: BRAND_COLORS.PRIMARY,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: BRAND_COLORS.PRIMARY_DARK
            }
          }}
        >
          {isAssigning ? (isLoading ? 'Generando PDF...' : 'Asignando...') : 'Confirmar Asignación y Descargar PDF'}
        </Button>
      </DialogActions>
      </Dialog>

      <AppDialog
        open={confirmVisible}
        onClose={() => {
          setConfirmVisible(false);
          confirmResolverRef.current?.(false);
        }}
        title='Confirmación'
        maxWidth='xs'
        actions={
          <>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(false);
              }}
              variantType='secondary'>
              Cancelar
            </AppButton>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(true);
              }}>
              Aceptar
            </AppButton>
          </>
        }>
        <Typography>{confirmMessage}</Typography>
      </AppDialog>
    </>
  );
};

export default ComponentExternoEngastadorBulkModal;
